import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputTextarea} from "primereact/inputtextarea";
import {Message} from "primereact/message";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";

import type {ValuationData} from "@reva/shared/valuation-data";

import type {ArchiveDocumentType} from "../../api/payload-types/persistence-finalize";
import OverriddenVariantAppreciationsWarning from "../../component/appreciation/OverriddenVariantAppreciationsWarning";
import I18nErrorMessage from "../../component/I18nErrorMessage";
import {selectUsername} from "../../redux/authSlice";
import {
    removePreFinalizeError,
    selectLastValidPersistenceResponse,
    selectPendingWrites,
    selectPreFinalizeError,
    selectPreFinalizeLoading,
    selectRevaId,
    setPreFinalizeLoading,
} from "../../redux/persistenceSlice";
import {fetchPersistenceByIdInternal, preFinalizePersistence} from "../../redux/persistenceThunk";
import {useRevaDispatch, useRevaSelector} from "../../redux/revaHooks";
import type {ApiQueryError} from "../../redux/types";
import {buildApiQueryError, generateDefaultQueryError} from "../../util/errorUtil";
import {checkForPersistenceChanges} from "../../util/persistenceChangeUtil";
import ArchiveDocumentsSelection, {
    defaultDocTypeSelection,
    useAllowedArchiveDocumentTypes,
} from "./ArchiveDocumentsSelection";
import {useArchivedDocumentsHandler} from "./DialogArchivedDocuments";
import DialogFinalizeValuationChanged from "./DialogFinalizeValuationChanged";

interface Props {
    /** Ob der Dialog sichtbar ist */
    visible: boolean;
    /** Setter für die Sichtbarkeit des Dialogs */
    setVisible: (visible: boolean) => void;
    /** Callback nach erfolgreichem Vor-Finalisieren, das dem aktuellen User die Bewertung entzogen hat. */
    onHandedOff?: () => void;
}

/**
 * Dialog to pre-finalize a Valuation.
 */
export default function DialogPreFinalizeValuation({visible, setVisible, onHandedOff}: Props) {
    const {t} = useTranslation();
    const dispatch = useRevaDispatch();

    const revaId = useRevaSelector(selectRevaId);
    const currentUsername = useRevaSelector(selectUsername);
    const lastValidPersistenceResponse = useRevaSelector(selectLastValidPersistenceResponse);
    const preFinalizeLoading = useRevaSelector(selectPreFinalizeLoading);
    const preFinalizeError = useRevaSelector(selectPreFinalizeError);
    const pendingWrites = useRevaSelector(selectPendingWrites);

    const allowedDocTypes = useAllowedArchiveDocumentTypes();
    const {handleArchivedDocuments, archivedDocumentsDialog} = useArchivedDocumentsHandler();

    const [persistenceSnapshot, setPersistenceSnapshot] = useState<ValuationData | null>(null);
    const [persistenceFromApi, setPersistenceFromApi] = useState<ValuationData | null>(null);
    const [persistenceLoadError, setPersistenceLoadError] = useState<ApiQueryError | null>(null);
    const [hasPersistenceChanged, setHasPersistenceChanged] = useState(false);
    const [comment, setComment] = useState("");
    const [selectedDocTypes, setSelectedDocTypes] = useState<ArchiveDocumentType[]>([]);
    /** Merker, damit der Vergleichsstand pro Öffnen nur einmal genommen wird. */
    const snapshotTaken = useRef(false);

    // Beim Öffnen: Snapshot aufnehmen und frische Daten vom Backend laden.
    // Warten, bis keine eigenen Schreibvorgänge mehr laufen (pendingWrites).
    // Beim Schließen: lokalen State aufräumen.
    useEffect(() => {
        if (!visible) {
            snapshotTaken.current = false;
            setPersistenceSnapshot(null);
            setPersistenceFromApi(null);
            setPersistenceLoadError(null);
            setHasPersistenceChanged(false);
            setComment("");
            setSelectedDocTypes([]);
            return;
        }
        // Erst vergleichen, wenn die eigenen Schreibvorgänge durch sind: solange noch ein Auto-Save
        // unterwegs ist, kennt lastValidPersistenceResponse die gerade eingegebene Änderung nicht, und
        // der Server meldet sie als fremde Änderung zurück ("Änderungen erkannt" auf den eigenen Wert).
        // Bis dahin bleibt persistenceFromApi null, der Dialog zeigt also seinen Lade-Spinner.
        if (pendingWrites > 0 || snapshotTaken.current) {
            return;
        }
        snapshotTaken.current = true;
        // Vorauswahl: keine Dokumente — außer es gibt genau einen erlaubten Typ.
        setSelectedDocTypes(defaultDocTypeSelection(allowedDocTypes));
        const snapshot = lastValidPersistenceResponse as ValuationData;
        setPersistenceSnapshot(snapshot);
        dispatch(fetchPersistenceByIdInternal(revaId!, true))
            .then((apiData) => {
                setPersistenceFromApi(apiData);
                setHasPersistenceChanged(checkForPersistenceChanges(apiData, snapshot));
            })
            .catch((err) => setPersistenceLoadError(buildApiQueryError(generateDefaultQueryError(err as Error))));
    }, [visible, pendingWrites]); // eslint-disable-line react-hooks/exhaustive-deps

    const onClose = () => {
        dispatch(removePreFinalizeError());
        setVisible(false);
    };

    const onPreFinalize = async () => {
        dispatch(removePreFinalizeError());
        dispatch(setPreFinalizeLoading(true));
        // Owner unmittelbar VOR dem Vor-Finalisieren (das Backend nullt ihn dabei) — für den
        // "dir nicht mehr zugeordnet"-Toast, der nur erscheinen soll, wenn es der eigene war.
        let ownerBefore: string | null = null;
        await dispatch(fetchPersistenceByIdInternal(revaId!, true))
            .then((persistence) => {
                setPersistenceFromApi(persistence);
                ownerBefore = persistence?.metadata?.owner ?? null;
                const changed = checkForPersistenceChanges(persistence, persistenceSnapshot!);
                setHasPersistenceChanged(changed);
                return changed;
            })
            .then(async (hasChanged: boolean) => {
                if (!hasChanged) {
                    // Erfolg = Thunk liefert die Response (nicht null) — NICHT an finalDocument
                    // festmachen, das ist bei leerer Dokumentauswahl null.
                    const response = await dispatch(preFinalizePersistence(comment || null, selectedDocTypes));
                    if (response) {
                        // Erfolg: Dialog schließen. Wurde genau EIN Dokument erzeugt, wird es direkt
                        // geöffnet; bei mehreren erscheint der Download-Dialog (s. Hook). Unabhängig
                        // davon sind die Dokumente jederzeit über das Snapshot-Untermenü des
                        // Lifecycle-Buttons abrufbar.
                        handleArchivedDocuments(response);
                        onClose();
                        if (ownerBefore === currentUsername) {
                            onHandedOff?.();
                        }
                    }
                } else {
                    dispatch(setPreFinalizeLoading(false));
                }
            })

            .catch((err: unknown) => {
                dispatch(setPreFinalizeLoading(false));
                setPersistenceLoadError(buildApiQueryError(generateDefaultQueryError(err as Error)));
            });
    };

    // Kein (Pre-)Finalize-Dialog anzeigen wenn nicht sichtbar — aber der Download-Dialog
    // der soeben erzeugten Dokumente öffnet sich genau NACH dem Schließen (ist null,
    // solange keine Dokumente anzubieten sind).
    if (!visible) return archivedDocumentsDialog;

    // Busy-Indikator während des initialen Ladens
    if (!persistenceFromApi && !persistenceLoadError) {
        return (
            <Dialog
                id="preFinalizeDialog"
                header={t("preFinalizeDialog.title")}
                style={{minWidth: "50rem"}}
                visible={true}
                onHide={() => {}}
                closable={false}
            >
                <div className="flex justify-center">
                    <div className="pi pi-spin pi-spinner text-6xl" />
                </div>
            </Dialog>
        );
    }

    // Fehler beim initialen Laden: schließbarer Dialog mit Fehlermeldung
    if (persistenceLoadError) {
        return (
            <Dialog
                id="preFinalizeDialog"
                header={t("preFinalizeDialog.title")}
                style={{minWidth: "50rem"}}
                visible={true}
                onHide={onClose}
            >
                <I18nErrorMessage errorObject={persistenceLoadError} className="block" />
            </Dialog>
        );
    }

    if (hasPersistenceChanged) {
        return (
            <DialogFinalizeValuationChanged
                persistenceFromApi={persistenceFromApi!}
                persistenceSnapshot={persistenceSnapshot!}
                onClose={() => setVisible(false)}
            />
        );
    }

    const footerForm = (
        <>
            <Button
                id="preFinalizeCancelButton"
                label={t("preFinalizeDialog.cancel")}
                icon={PrimeIcons.TIMES}
                severity="danger"
                className="mr-2"
                text
                onClick={onClose}
                disabled={preFinalizeLoading}
            />
            <Button
                id="preFinalizeSubmitButton"
                label={t("preFinalizeDialog.submit")}
                icon={PrimeIcons.CHECK}
                onClick={() => void onPreFinalize()}
                loading={preFinalizeLoading}
                // Auch hier sperren, solange ein Schreibvorgang läuft: onPreFinalize vergleicht erneut
                // gegen lastValidPersistenceResponse und würde sonst dieselbe Falle aufmachen.
                disabled={preFinalizeLoading || pendingWrites > 0}
            />
        </>
    );

    return (
        <Dialog
            id="preFinalizeDialog"
            header={t("preFinalizeDialog.title")}
            style={{minWidth: "50rem", minHeight: "10rem"}}
            visible={visible}
            onHide={() => setVisible(false)}
            closable={false}
            footer={footerForm}
        >
            <div className="flex flex-col gap-4">
                <Message
                    severity="warn"
                    text={t("preFinalizeDialog.confirmText")}
                    className="block white-space-pre-line leading-normal"
                />
                <OverriddenVariantAppreciationsWarning />
                <ArchiveDocumentsSelection
                    options={allowedDocTypes}
                    selected={selectedDocTypes}
                    setSelected={setSelectedDocTypes}
                    disabled={preFinalizeLoading}
                    idPrefix="preFinalize"
                />
                <div className="flex flex-col gap-2">
                    <label htmlFor="preFinalizeComment">{t("preFinalizeDialog.commentLabel")}</label>
                    <InputTextarea
                        id="preFinalizeComment"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder={t("preFinalizeDialog.commentPlaceholder")}
                        rows={3}
                        autoResize
                        disabled={preFinalizeLoading}
                    />
                </div>
                <I18nErrorMessage errorObject={preFinalizeError} className="block" />
            </div>
        </Dialog>
    );
}
